if (!customElements.get("product-modal")) {
  customElements.define(
    "product-modal",
    class ProductModal extends ModalDialog {
      constructor() {
        super();

        this.closeEvent = new CustomEvent("product:modal-close");
        this.openEvent = new CustomEvent("product:modal-open");

        // SwiftMoto: on phones and tablets the photos in the viewer sit in a
        // row that you swipe through (layout in section-main-product.css).
        this.swipeQuery = window.matchMedia("(max-width: 989px)");
        this.content = this.querySelector(".product-media-modal-content");
        this.swipeIndex = 0;
        this.content?.addEventListener("scroll", this.onSwipeScroll.bind(this), {
          passive: true,
        });
        // Load the full-size photo when the shopper zooms in.
        window.visualViewport?.addEventListener("resize", this.upgradeIfZoomed.bind(this));
      }

      hide() {
        this.syncGallery();
        super.hide();
        document.body.classList.remove("product-media-modal-open");
        document.dispatchEvent(this.closeEvent);
      }

      show(opener) {
        super.show(opener);
        document.body.classList.add("product-media-modal-open");
        this.showActiveMedia();
        document.dispatchEvent(this.openEvent);
      }

      showActiveMedia() {
        this.querySelectorAll(
          `[data-media-id]:not([data-media-id="${this.openedBy.getAttribute("data-media-id")}"])`,
        ).forEach((element) => {
          element.classList.remove("is-active");
        });
        const activeMedia = this.querySelector(
          `[data-media-id="${this.openedBy.getAttribute("data-media-id")}"]`,
        );
        const activeMediaTemplate = activeMedia.querySelector("template");
        const activeMediaContent = activeMediaTemplate ? activeMediaTemplate.content : null;
        activeMedia.classList.add("is-active");
        activeMedia.scrollIntoView();

        const container = this.querySelector('[role="document"]');
        container.scrollLeft = (activeMedia.width - container.clientWidth) / 2;

        if (
          activeMedia.nodeName == "DEFERRED-MEDIA" &&
          activeMediaContent &&
          activeMediaContent.querySelector(".js-youtube")
        ) {
          activeMedia.loadContent();
        }

        this.showSwipeMedia(activeMedia);
      }

      /* SwiftMoto: swipe between photos (phones and tablets) ------------- */

      // The photos in the viewer, in the same order as the slider on the page.
      getSwipeItems() {
        if (!this.content) {
          return [];
        }
        return Array.from(this.content.children).filter((element) => element.dataset.mediaId);
      }

      isSwipeMode() {
        return this.swipeQuery.matches && this.getSwipeItems().length > 1;
      }

      getSectionId() {
        return this.id.replace(/^ProductModal-/, "");
      }

      // Opens the row on the tapped photo.
      showSwipeMedia(activeMedia) {
        if (!this.isSwipeMode()) {
          this.counter?.setAttribute("hidden", "");
          return;
        }
        const items = this.getSwipeItems();
        // Set the index first, so the scroll below doesn't count as a swipe.
        this.swipeIndex = Math.max(items.indexOf(activeMedia), 0);
        this.content.scrollLeft = this.content.clientWidth * this.swipeIndex;
        this.updateSwipe(items);
      }

      onSwipeScroll() {
        if (this.swipeFrame) {
          return;
        }
        this.swipeFrame = requestAnimationFrame(() => {
          this.swipeFrame = null;
          if (!this.hasAttribute("open") || !this.isSwipeMode()) {
            return;
          }
          const items = this.getSwipeItems();
          const width = this.content.clientWidth || 1;
          const index = Math.min(
            Math.max(Math.round(this.content.scrollLeft / width), 0),
            items.length - 1,
          );
          if (index === this.swipeIndex) {
            return;
          }
          this.swipeIndex = index;
          this.updateSwipe(items);
          window.pauseAllMedia?.();
          // Lets the product page tracking count swipes (pdp-tracking.js).
          document.dispatchEvent(
            new CustomEvent("product:modal-swipe", {
              detail: {
                sectionId: this.getSectionId(),
                mediaId: items[index].dataset.mediaId,
                index: index,
                total: items.length,
              },
            }),
          );
        });
      }

      // Counter, active photo, and loading the photos either side early.
      updateSwipe(items) {
        if (!this.counter) {
          this.counter = document.createElement("div");
          this.counter.className = "product-media-modal-counter";
          this.counter.setAttribute("aria-live", "polite");
          this.querySelector(".product-media-modal-dialog")?.appendChild(this.counter);
        }
        this.counter.removeAttribute("hidden");
        this.counter.textContent = `${this.swipeIndex + 1} / ${items.length}`;

        items.forEach((item, index) => {
          item.classList.toggle("is-active", index === this.swipeIndex);
        });

        [items[this.swipeIndex - 1], items[this.swipeIndex + 1]].forEach((item) => {
          if (item && item.tagName === "IMG" && item.loading === "lazy") {
            item.loading = "eager";
          }
        });

        this.upgradeIfZoomed();
      }

      // On phones the viewer shows the same smaller photos as the slider
      // (snippets/product-gallery-media-modal.liquid). When the shopper
      // pinches or double taps to zoom in, load the full-size photo in the
      // background and swap it in once it has arrived, so nothing goes blank.
      // Only the photo being looked at, and only once.
      upgradeIfZoomed() {
        if (!this.hasAttribute("open") || !this.isSwipeMode()) {
          return;
        }
        if (!window.visualViewport || window.visualViewport.scale < 1.1) {
          return;
        }
        const item = this.getSwipeItems()[this.swipeIndex];
        if (!item || item.tagName !== "IMG" || item.dataset.fullSize) {
          return;
        }
        const candidates = (item.getAttribute("srcset") || "")
          .split(",")
          .map((entry) => entry.trim().split(/\s+/))
          .filter((parts) => parts.length === 2 && /^\d+w$/.test(parts[1]))
          .map(([url, width]) => ({ url, width: parseInt(width, 10) }));
        if (!candidates.length) {
          return;
        }
        // The 2048px copy, or the biggest listed (Shopify never sends more
        // than the original photo).
        const full =
          candidates.find((candidate) => candidate.width === 2048) ||
          candidates[candidates.length - 1];
        item.dataset.fullSize = "loading";
        const loader = new Image();
        loader.onload = () => {
          item.srcset = `${full.url} ${full.width}w`;
          item.dataset.fullSize = "done";
        };
        loader.onerror = () => {
          delete item.dataset.fullSize;
        };
        loader.src = full.url;
      }

      // On close, move the photo slider on the page to the last photo seen.
      syncGallery() {
        if (!this.hasAttribute("open") || !this.isSwipeMode()) {
          return;
        }
        const item = this.getSwipeItems()[this.swipeIndex];
        const openedId = this.openedBy?.getAttribute("data-media-id");
        if (!item || item.dataset.mediaId === openedId) {
          return;
        }
        const gallery = document.getElementById(`MediaGallery-${this.getSectionId()}`);
        try {
          gallery?.setActiveMedia?.(item.dataset.mediaId, false);
        } catch (error) {
          // The slider is optional; closing the viewer must still work.
        }
      }
    },
  );
}
